import React from 'react';
import {SPLASH_TIMING, type SplashStage} from './constants';

export type SplashPhase = 'visible' | 'closing' | 'fading' | 'gone';
export type SplashExit = 'handoff' | 'dismiss' | null;

type UseSplashControllerProps = {
  isReady: boolean;
  shouldDismiss: boolean;
  minVisibleMs?: number;
  closingMs?: number;
};

const TICK_MS = 100;

function deriveStage(elapsedMs: number): SplashStage {
  if (elapsedMs >= SPLASH_TIMING.slow) return 'slow';
  if (elapsedMs >= SPLASH_TIMING.almost) return 'almost';
  if (elapsedMs >= SPLASH_TIMING.loading) return 'loading';
  if (elapsedMs >= SPLASH_TIMING.quiet) return 'connecting';
  return 'quiet';
}

function useSplashController({
  isReady,
  shouldDismiss,
  minVisibleMs = SPLASH_TIMING.minVisible,
  closingMs = SPLASH_TIMING.curtain,
}: UseSplashControllerProps) {
  const [phase, setPhase] = React.useState<SplashPhase>(() =>
    shouldDismiss ? 'gone' : 'visible',
  );
  const [exit, setExit] = React.useState<SplashExit>(null);
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const startRef = React.useRef(Date.now());

  React.useEffect(
    function tickWhileVisible() {
      if (phase !== 'visible') {
        return;
      }

      const interval = setInterval(() => {
        setElapsedMs(Date.now() - startRef.current);
      }, TICK_MS);

      return () => clearInterval(interval);
    },
    [phase],
  );

  const isWatchdogFired = elapsedMs >= SPLASH_TIMING.watchdog;

  React.useEffect(
    function dismissWithoutHandoff() {
      if (phase !== 'visible' || (!shouldDismiss && !isWatchdogFired)) {
        return;
      }

      setExit('dismiss');
      setPhase('fading');
    },
    [phase, shouldDismiss, isWatchdogFired],
  );

  React.useEffect(
    function handOffWhenReady() {
      if (phase !== 'visible' || !isReady || shouldDismiss) {
        return;
      }

      const held = Date.now() - startRef.current;
      const wait = Math.max(0, minVisibleMs - held);
      const timeout = setTimeout(() => {
        setExit('handoff');
        setPhase('closing');
      }, wait);

      return () => clearTimeout(timeout);
    },
    [phase, isReady, shouldDismiss, minVisibleMs],
  );

  React.useEffect(
    function advanceExit() {
      if (phase !== 'closing' && phase !== 'fading') {
        return;
      }

      const next: SplashPhase = phase === 'closing' ? 'fading' : 'gone';
      const duration = phase === 'closing' ? closingMs : SPLASH_TIMING.curtain;
      const timeout = setTimeout(() => setPhase(next), duration);

      return () => clearTimeout(timeout);
    },
    [phase, closingMs],
  );

  const restart = React.useCallback(() => {
    startRef.current = Date.now();
    setElapsedMs(0);
    setExit(null);
    setPhase('visible');
  }, []);

  return {
    phase,
    exit,
    stage: deriveStage(elapsedMs),
    elapsedMs,
    restart,
  };
}

export {useSplashController};
